import React, { useState, useMemo } from 'react';
import {
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area,
    Line
} from 'recharts';
import { ForecastResponse } from '../types/api';

export type { ForecastResponse };

interface ForecastViewProps {
    data: ForecastResponse | null;
    loading: boolean;
    error: any;
    selectedWarehouse: string;
    onRefresh: () => void;
}

interface MonthlyProjection {
    month: string;
    monthKey: string;
    daysInMonth: number;
    openingStock: number;
    consumption: number;
    ordersPlaced: number;
    orderQuantity: number;
    arrivals: number;
    closingStock: number;
}

interface DropdownOption {
    value: string;
    label: string;
}

interface CustomDropdownProps {
    value: string;
    onChange: (value: string) => void;
    options: DropdownOption[];
    disabled?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ value, onChange, options, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((option) => option.value === value) || options[0];

    return (
        <div className="relative" tabIndex={0} onBlur={() => setIsOpen(false)}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen((prev) => !prev)}
                className={`w-full px-3 py-1.5 text-sm border rounded-md flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    disabled
                        ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white border-gray-300 text-gray-700'
                }`}
            >
                <span>{selectedOption?.label || ''}</span>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                    {options.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                onChange(option.value);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors ${option.value === value
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// COMPONENT
// ============================================================================

const ForecastView: React.FC<ForecastViewProps> = ({
    data: forecastData,
    loading,
    error,
    selectedWarehouse,
    onRefresh
}) => {
    // Local state for product filtering (client-side)
    const [selectedProduct, setSelectedProduct] = useState('all');
    const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
    const [viewType, setViewType] = useState<'daily' | 'monthly'>('monthly');
    const [displayMode, setDisplayMode] = useState<'table' | 'graph'>('table');
    const [graphGranularity, setGraphGranularity] = useState<'monthly' | 'daily'>('monthly');

    // Get unique products for filters
    const filterOptions = useMemo(() => {
        if (!forecastData) return { products: [] };
        const products = Array.from(
            new Set(
                forecastData.forecasts
                    .map(f => f.product_name)
                    .filter((name): name is string => name !== null)
            )
        );
        return { products };
    }, [forecastData]);

    // Client-side filtering
    const filteredForecasts = useMemo(() => {
        if (!forecastData) return [];

        let filtered = forecastData.forecasts;

        // Warehouse filtering is handled by the parent via API/Prop, 
        // but if the API returns mixed data we can filter here too.
        if (selectedWarehouse !== 'all') {
            filtered = filtered.filter(f => f.warehouse === selectedWarehouse);
        }

        if (selectedProduct !== 'all') {
            filtered = filtered.filter(f => f.product_name === selectedProduct);
        }

        return filtered;
    }, [forecastData, selectedWarehouse, selectedProduct]);

    // Calculate filtered summary
    const filteredSummary = useMemo(() => {
        if (filteredForecasts.length === 0) return null;

        const byQuarter: Record<string, { orders: number; quantity: number }> = {};

        filteredForecasts.forEach(forecast => {
            forecast.forecasted_orders.forEach(order => {
                if (!byQuarter[order.quarter]) {
                    byQuarter[order.quarter] = { orders: 0, quantity: 0 };
                }
                byQuarter[order.quarter].orders += 1;
                byQuarter[order.quarter].quantity += order.quantity;
            });
        });

        return {
            total_products: filteredForecasts.length,
            total_forecasted_orders: filteredForecasts.reduce((sum, f) => sum + f.total_orders, 0),
            total_forecasted_quantity: filteredForecasts.reduce((sum, f) => sum + f.total_quantity, 0),
            by_quarter: byQuarter,
        };
    }, [filteredForecasts]);

    // Calculate full 12-month projection with stock flow
    const monthlyProjections = useMemo(() => {
        if (filteredForecasts.length === 0) return [];

        // Get total current stock and DRR across all filtered products
        const totalCurrentStock = filteredForecasts.reduce((sum, f) => sum + f.current_stock, 0);
        const totalDRR = filteredForecasts.reduce((sum, f) => sum + f.daily_run_rate, 0);

        // Build order and arrival data by month
        const ordersByMonth: Record<string, { orders: number; quantity: number }> = {};
        const arrivalsByMonth: Record<string, number> = {};

        filteredForecasts.forEach(forecast => {
            forecast.forecasted_orders.forEach(order => {
                // Orders placed
                const orderDate = new Date(order.order_date);
                const orderMonthKey = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`;
                if (!ordersByMonth[orderMonthKey]) {
                    ordersByMonth[orderMonthKey] = { orders: 0, quantity: 0 };
                }
                ordersByMonth[orderMonthKey].orders += 1;
                ordersByMonth[orderMonthKey].quantity += order.quantity;

                // Arrivals (order arrives after lead time)
                const arrivalDate = new Date(order.arrival_date);
                const arrivalMonthKey = `${arrivalDate.getFullYear()}-${String(arrivalDate.getMonth() + 1).padStart(2, '0')}`;
                if (!arrivalsByMonth[arrivalMonthKey]) {
                    arrivalsByMonth[arrivalMonthKey] = 0;
                }
                arrivalsByMonth[arrivalMonthKey] += order.quantity;
            });
        });

        // Generate 12 months of projections
        const projections: MonthlyProjection[] = [];
        const today = new Date();
        let runningStock = totalCurrentStock;

        for (let i = 0; i < 12; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
            const monthStr = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            
            // Days in this month
            const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
            
            // For first month, adjust for days remaining
            const effectiveDays = i === 0 ? Math.max(1, daysInMonth - today.getDate() + 1) : daysInMonth;
            
            // Opening stock
            const openingStock = Math.max(0, Math.round(runningStock));
            
            // Arrivals this month
            const arrivals = arrivalsByMonth[monthKey] || 0;
            
            // Add arrivals to stock
            runningStock += arrivals;
            
            // Monthly consumption
            const consumption = Math.round(totalDRR * effectiveDays);
            
            // Subtract consumption
            runningStock -= consumption;
            
            // Closing stock (can't go below 0)
            const closingStock = Math.max(0, Math.round(runningStock));
            runningStock = closingStock;
            
            // Orders placed this month
            const monthOrders = ordersByMonth[monthKey] || { orders: 0, quantity: 0 };

            projections.push({
                month: monthStr,
                monthKey,
                daysInMonth: effectiveDays,
                openingStock,
                consumption,
                ordersPlaced: monthOrders.orders,
                orderQuantity: monthOrders.quantity,
                arrivals,
                closingStock
            });
        }

        return projections;
    }, [filteredForecasts]);

    // Calculate daily projection for graph (365 days)
    const dailyProjections = useMemo(() => {
        if (filteredForecasts.length === 0) return [];

        const totalCurrentStock = filteredForecasts.reduce((sum, f) => sum + f.current_stock, 0);
        const totalDRR = filteredForecasts.reduce((sum, f) => sum + f.daily_run_rate, 0);

        // Build arrivals by day
        const arrivalsByDay: Record<number, number> = {};
        const ordersByDay: Record<number, number> = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        filteredForecasts.forEach(forecast => {
            forecast.forecasted_orders.forEach(order => {
                // Orders placed
                const orderDate = new Date(order.order_date);
                const orderDayNum = Math.floor((orderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                if (orderDayNum >= 0 && orderDayNum < 365) {
                    ordersByDay[orderDayNum] = (ordersByDay[orderDayNum] || 0) + order.quantity;
                }

                // Arrivals
                const arrivalDate = new Date(order.arrival_date);
                const arrivalDayNum = Math.floor((arrivalDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                if (arrivalDayNum >= 0 && arrivalDayNum < 365) {
                    arrivalsByDay[arrivalDayNum] = (arrivalsByDay[arrivalDayNum] || 0) + order.quantity;
                }
            });
        });

        // Generate daily data points (sample every 7 days for performance)
        const projections: Array<{
            day: number;
            date: string;
            fullDate: string;
            stock: number;
            dailyRate: number;
            consumption: number;
            arrivals: number;
            orders: number;
        }> = [];

        let runningStock = totalCurrentStock;

        for (let day = 0; day < 365; day++) {
            const currentDate = new Date(today);
            currentDate.setDate(currentDate.getDate() + day);
            
            // Add arrivals
            const arrivals = arrivalsByDay[day] || 0;
            runningStock += arrivals;
            
            // Subtract consumption
            runningStock -= totalDRR;
            runningStock = Math.max(0, runningStock);

            const orders = ordersByDay[day] || 0;

            // Sample every 7 days for graph performance, but always include days with events
            if (day % 7 === 0 || arrivals > 0 || orders > 0) {
                projections.push({
                    day,
                    date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    fullDate: currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
                    stock: Math.round(runningStock),
                    dailyRate: Math.round(totalDRR),
                    consumption: Math.round(totalDRR),
                    arrivals: Math.round(arrivals),
                    orders: Math.round(orders)
                });
            }
        }

        return projections;
    }, [filteredForecasts]);

    // Prepare monthly data for graph
    const monthlyGraphData = useMemo(() => {
        return monthlyProjections.map(proj => ({
            month: proj.month.split(' ')[0].substring(0, 3), // "Jan", "Feb", etc.
            fullMonth: proj.month,
            openingStock: proj.openingStock,
            closingStock: proj.closingStock,
            consumption: proj.consumption,
            arrivals: proj.arrivals,
            orders: proj.orderQuantity,
            daysInMonth: proj.daysInMonth
        }));
    }, [monthlyProjections]);

    // Toggle product expansion
    const toggleProduct = (key: string) => {
        setExpandedProducts(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Format date for display
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Get priority color based on days from now
    const getDaysColor = (days: number) => {
        if (days <= 14) return 'text-red-600 font-bold';
        if (days <= 30) return 'text-orange-600 font-semibold';
        if (days <= 60) return 'text-yellow-600';
        return 'text-gray-600';
    };

    // Sort quarters chronologically
    const sortedQuarters = useMemo(() => {
        if (!filteredSummary) return [];
        return Object.entries(filteredSummary.by_quarter).sort((a, b) => {
            // Parse "Q1 2026" format
            const [qA, yearA] = a[0].split(' ');
            const [qB, yearB] = b[0].split(' ');
            if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
            return parseInt(qA.slice(1)) - parseInt(qB.slice(1));
        });
    }, [filteredSummary]);

    return (
        <div className="w-full">
            {/* Summary Cards */}
            {filteredSummary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Products Tracked</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">
                            {filteredSummary.total_products}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-blue-600">Forecasted Orders</div>
                        <div className="text-2xl font-bold text-blue-600 mt-1">
                            {filteredSummary.total_forecasted_orders}
                        </div>
                        <div className="text-xs text-gray-400">annual projection</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-green-600">Total Quantity</div>
                        <div className="text-2xl font-bold text-green-600 mt-1">
                            {filteredSummary.total_forecasted_quantity.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-400">units</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-purple-600">Lead Time</div>
                        <div className="text-2xl font-bold text-purple-600 mt-1">
                            {forecastData?.parameters?.lead_time_days || 70}
                        </div>
                        <div className="text-xs text-gray-400">days (mfg + ship)</div>
                    </div>
                </div>
            )}

            {/* Quarterly Summary */}
            {sortedQuarters.length > 0 && (
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Orders by Quarter</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {sortedQuarters.map(([quarter, data]) => (
                            <div key={quarter} className="bg-gray-50 rounded-lg p-3 text-center">
                                <div className="text-xs text-gray-500 mb-1">{quarter}</div>
                                <div className="text-lg font-bold text-gray-900">{data.orders}</div>
                                <div className="text-xs text-gray-500">
                                    {data.quantity.toLocaleString()} units
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap items-end gap-3">
                    {/* Display Mode Toggle (Table/Graph) */}
                    <div className="flex-shrink-0">
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Display</label>
                        <div className="flex bg-gray-100 p-0.5 rounded-md">
                            <button
                                onClick={() => setDisplayMode('table')}
                                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                                    displayMode === 'table'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Table
                            </button>
                            <button
                                onClick={() => setDisplayMode('graph')}
                                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                                    displayMode === 'graph'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Graph
                            </button>
                        </div>
                    </div>

                    {/* View Type Toggle - Only show for Table mode */}
                    {displayMode === 'table' && (
                        <div className="flex-shrink-0">
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">View</label>
                            <div className="flex bg-gray-100 p-0.5 rounded-md">
                                <button
                                    onClick={() => setViewType('monthly')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                                        viewType === 'monthly'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setViewType('daily')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                                        viewType === 'daily'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Daily
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Graph Granularity - Only show for Graph mode */}
                    {displayMode === 'graph' && (
                        <div className="flex-shrink-0">
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Granularity</label>
                            <div className="flex bg-gray-100 p-0.5 rounded-md">
                                <button
                                    onClick={() => setGraphGranularity('monthly')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                                        graphGranularity === 'monthly'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setGraphGranularity('daily')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                                        graphGranularity === 'daily'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Daily
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex-1 min-w-[180px]">
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Product</label>
                        <CustomDropdown
                            value={selectedProduct}
                            onChange={setSelectedProduct}
                            disabled={filterOptions.products.length <= 1}
                            options={[
                                { value: 'all', label: 'All Products' },
                                ...filterOptions.products.map((prod) => ({ value: prod, label: prod }))
                            ]}
                        />
                    </div>

                    <button
                        onClick={onRefresh}
                        disabled={loading}
                        className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
                    >
                        {loading ? 'Refreshing...' : 'Refresh Forecast'}
                    </button>
                </div>
                {filterOptions.products.length <= 1 && (
                    <div className="text-[11px] text-gray-500 mt-1">Only one product in current data</div>
                )}
            </div>

            {/* Loading State - Skeleton UI */}
            {loading && !forecastData && (
                <div className="space-y-6">
                    <div className="flex items-center justify-center py-4">
                        <div className="flex items-center space-x-2 text-gray-500">
                            <span className="ml-3 text-sm">Generating forecast...</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                    {error instanceof Error ? error.message : 'Failed to load forecast'}
                </div>
            )}

            {/* Professional Graph View */}
            {!loading && !error && displayMode === 'graph' && filteredForecasts.length > 0 && (
                <div className="space-y-4">
                    {/* KPI Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Stock</span>
                                <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                </div>
                            </div>
                            <div className="mt-2">
                                <span className="text-2xl font-bold text-gray-900">
                                    {filteredForecasts.reduce((sum, f) => sum + f.current_stock, 0).toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500 ml-1">units</span>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Annual Consumption</span>
                                <div className="w-8 h-8 bg-red-50 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                    </svg>
                                </div>
                            </div>
                            <div className="mt-2">
                                <span className="text-2xl font-bold text-gray-900">
                                    {monthlyProjections.reduce((sum, m) => sum + m.consumption, 0).toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500 ml-1">units</span>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Arrivals</span>
                                <div className="w-8 h-8 bg-purple-50 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
                                    </svg>
                                </div>
                            </div>
                            <div className="mt-2">
                                <span className="text-2xl font-bold text-gray-900">
                                    {monthlyProjections.reduce((sum, m) => sum + m.arrivals, 0).toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500 ml-1">units</span>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">End of Year Stock</span>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    monthlyProjections[monthlyProjections.length - 1]?.closingStock > 5000 
                                        ? 'bg-green-50' : 'bg-yellow-50'
                                }`}>
                                    <svg className={`w-4 h-4 ${
                                        monthlyProjections[monthlyProjections.length - 1]?.closingStock > 5000 
                                            ? 'text-green-600' : 'text-yellow-600'
                                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="mt-2">
                                <span className="text-2xl font-bold text-gray-900">
                                    {(monthlyProjections[monthlyProjections.length - 1]?.closingStock || 0).toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500 ml-1">units</span>
                            </div>
                        </div>
                    </div>

                    {/* Main Chart Container - Clean Line Style */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Chart */}
                        <div className="p-4 pt-6">
                            <div className="h-[380px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart 
                                        data={graphGranularity === 'monthly' ? monthlyGraphData : dailyProjections} 
                                        margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                                    >
                                        <defs>
                                            <linearGradient id="stockFillGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.6}/>
                                                <stop offset="50%" stopColor="#A78BFA" stopOpacity={0.3}/>
                                                <stop offset="100%" stopColor="#C4B5FD" stopOpacity={0.05}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid 
                                            strokeDasharray="3 3" 
                                            stroke="#E5E7EB" 
                                            vertical={true}
                                            horizontal={true}
                                        />
                                        <XAxis 
                                            dataKey={graphGranularity === 'monthly' ? 'month' : 'date'}
                                            tick={{ fontSize: 10, fill: '#9CA3AF' }}
                                            axisLine={{ stroke: '#E5E7EB' }}
                                            tickLine={false}
                                            dy={5}
                                            interval={graphGranularity === 'daily' ? 'preserveStartEnd' : 0}
                                        />
                                        <YAxis 
                                            tick={{ fontSize: 10, fill: '#9CA3AF' }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={(value) => {
                                                if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
                                                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                                                return value.toString();
                                            }}
                                            width={40}
                                        />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#FFFFFF', 
                                                border: '1px solid #E5E7EB',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                                                padding: '0',
                                                fontSize: '12px'
                                            }}
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload || !payload.length) return null;
                                                
                                                const data = payload[0]?.payload;
                                                if (!data) return null;
                                                
                                                const isMonthly = graphGranularity === 'monthly';
                                                const stockLevel = isMonthly ? data.closingStock : data.stock;
                                                const consumption = data.consumption || 0;
                                                const arrivals = data.arrivals || 0;
                                                const openingStock = data.openingStock || 0;
                                                const dateLabel = isMonthly ? data.fullMonth : data.fullDate || label;
                                                const dailyRate = isMonthly 
                                                    ? consumption / (data.daysInMonth || 30)
                                                    : data.dailyRate || filteredForecasts.reduce((sum, f) => sum + f.daily_run_rate, 0);
                                                
                                                // Calculate days of stock
                                                const daysOfStock = dailyRate > 0 ? Math.round(stockLevel / dailyRate) : 999;
                                                
                                                // Determine status
                                                const status = stockLevel === 0 ? 'critical' : daysOfStock < 14 ? 'low' : daysOfStock < 30 ? 'warning' : 'healthy';
                                                const statusConfig = {
                                                    critical: { label: 'Stockout', color: 'bg-red-500', textColor: 'text-red-700' },
                                                    low: { label: 'Critical', color: 'bg-red-400', textColor: 'text-red-600' },
                                                    warning: { label: 'Low', color: 'bg-yellow-400', textColor: 'text-yellow-700' },
                                                    healthy: { label: 'Healthy', color: 'bg-green-400', textColor: 'text-green-700' }
                                                };
                                                
                                                return (
                                                    <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden min-w-[220px]">
                                                        {/* Header */}
                                                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <span className="font-semibold text-gray-900 text-sm">{dateLabel}</span>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig[status].color} text-white font-medium`}>
                                                                    {statusConfig[status].label}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Content */}
                                                        <div className="px-3 py-2 space-y-1.5">
                                                            {/* Stock Level */}
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-gray-500 text-xs">Stock Level</span>
                                                                <span className="font-semibold text-gray-900">{stockLevel.toLocaleString()}</span>
                                                            </div>
                                                            
                                                            {/* Days of Stock */}
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-gray-500 text-xs">Days of Stock</span>
                                                                <span className={`font-semibold ${statusConfig[status].textColor}`}>
                                                                    {stockLevel === 0 ? '0' : daysOfStock > 365 ? '365+' : daysOfStock} days
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Daily Run Rate */}
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-gray-500 text-xs">Daily Run Rate</span>
                                                                <span className="text-gray-700">{Math.round(dailyRate).toLocaleString()}/day</span>
                                                            </div>
                                                            
                                                            {/* Monthly specific data */}
                                                            {isMonthly && (
                                                                <>
                                                                    <div className="border-t border-gray-100 my-1"></div>
                                                                    
                                                                    {/* Opening Stock */}
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-gray-500 text-xs">Opening Stock</span>
                                                                        <span className="text-gray-700">{openingStock.toLocaleString()}</span>
                                                                    </div>
                                                                    
                                                                    {/* Arrivals */}
                                                                    {arrivals > 0 && (
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-gray-500 text-xs">Arrivals</span>
                                                                            <span className="text-green-600 font-medium">+{arrivals.toLocaleString()}</span>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Monthly Consumption */}
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-gray-500 text-xs">Monthly Consumption</span>
                                                                        <span className="text-gray-700">-{consumption.toLocaleString()}</span>
                                                                    </div>
                                                                </>
                                                            )}
                                                            
                                                            {/* Daily specific data */}
                                                            {!isMonthly && (
                                                                <>
                                                                    <div className="border-t border-gray-100 my-1"></div>
                                                                    
                                                                    {/* Arrivals */}
                                                                    {arrivals > 0 && (
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-gray-500 text-xs">Arrivals Today</span>
                                                                            <span className="text-green-600 font-medium">+{arrivals.toLocaleString()}</span>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Orders Placed */}
                                                                    {data.orders > 0 && (
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-gray-500 text-xs">Orders Placed</span>
                                                                            <span className="text-blue-600 font-medium">{data.orders.toLocaleString()}</span>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Day Number */}
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-gray-500 text-xs">Days from Today</span>
                                                                        <span className="text-gray-500">{data.day} days</span>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                        
                                        {/* Stock Level Area */}
                                        <Area 
                                            type="stepAfter" 
                                            dataKey={graphGranularity === 'monthly' ? 'closingStock' : 'stock'}
                                            fill="url(#stockFillGradient)" 
                                            stroke="#8B5CF6" 
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                        
                                        {/* Consumption Line */}
                                        {graphGranularity === 'monthly' && (
                                            <Line
                                                type="monotone"
                                                dataKey="consumption"
                                                stroke="#10B981"
                                                strokeWidth={1.5}
                                                dot={{ r: 2, fill: '#10B981', strokeWidth: 0 }}
                                                activeDot={{ r: 4, fill: '#10B981', stroke: '#FFFFFF', strokeWidth: 2 }}
                                            />
                                        )}
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-center gap-8">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center">
                                    <div className="w-4 h-4 rounded-sm" style={{ background: 'linear-gradient(180deg, rgba(139,92,246,0.6) 0%, rgba(196,181,253,0.1) 100%)' }}></div>
                                </div>
                                <span className="text-xs text-gray-600">Stock Level</span>
                            </div>
                            {graphGranularity === 'monthly' && (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-0.5 bg-emerald-500"></div>
                                    <span className="text-xs text-gray-600">Daily Consumption</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stockout Risk Indicator */}
                    {monthlyProjections.some(m => m.closingStock === 0) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-red-800">Stockout Risk Detected</h4>
                                    <p className="text-sm text-red-600 mt-1">
                                        Projected stockouts in: {monthlyProjections.filter(m => m.closingStock === 0).map(m => m.month.split(' ')[0]).join(', ')}. 
                                        Review order timing to avoid supply disruption.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Monthly Forecast View - Full 12-Month Projection (Table) */}
            {!loading && !error && displayMode === 'table' && viewType === 'monthly' && monthlyProjections.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b">
                        <h3 className="text-lg font-semibold text-gray-900">12-Month Inventory Forecast</h3>
                        <p className="text-sm text-gray-500 mt-1">Complete monthly projection showing stock flow, consumption, orders, and arrivals</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Opening Stock</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-purple-600 uppercase tracking-wider">Arrivals</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-red-500 uppercase tracking-wider">Consumption</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Closing Stock</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase tracking-wider">Orders Placed</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-green-600 uppercase tracking-wider">Order Qty</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {monthlyProjections.map((proj, index) => {
                                    const isLowStock = proj.closingStock < 5000;
                                    const isStockout = proj.closingStock === 0;
                                    return (
                                        <tr 
                                            key={proj.monthKey} 
                                            className={`hover:bg-gray-50 ${isStockout ? 'bg-red-50' : isLowStock ? 'bg-yellow-50' : ''}`}
                                        >
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{proj.month}</div>
                                                <div className="text-xs text-gray-400">{proj.daysInMonth} days</div>
                                                {index === 0 && (
                                                    <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mt-1">
                                                        Current
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                                {proj.openingStock.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                {proj.arrivals > 0 ? (
                                                    <span className="font-semibold text-purple-600">+{proj.arrivals.toLocaleString()}</span>
                                                ) : (
                                                    <span className="text-gray-300">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                <span className="text-red-500">-{proj.consumption.toLocaleString()}</span>
                                            </td>
                                            <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-semibold ${
                                                isStockout ? 'text-red-600' : isLowStock ? 'text-yellow-600' : 'text-gray-900'
                                            }`}>
                                                {proj.closingStock.toLocaleString()}
                                                {isStockout && <span className="ml-1 text-xs">(Stockout)</span>}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                {proj.ordersPlaced > 0 ? (
                                                    <span className="font-semibold text-blue-600">{proj.ordersPlaced}</span>
                                                ) : (
                                                    <span className="text-gray-300">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                {proj.orderQuantity > 0 ? (
                                                    <span className="font-semibold text-green-600">{proj.orderQuantity.toLocaleString()}</span>
                                                ) : (
                                                    <span className="text-gray-300">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                <tr>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        12-Month Total
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                                        -
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-bold text-purple-600">
                                        +{monthlyProjections.reduce((sum, m) => sum + m.arrivals, 0).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-bold text-red-500">
                                        -{monthlyProjections.reduce((sum, m) => sum + m.consumption, 0).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                                        {monthlyProjections[monthlyProjections.length - 1]?.closingStock.toLocaleString() || 0}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-600">
                                        {monthlyProjections.reduce((sum, m) => sum + m.ordersPlaced, 0)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-bold text-green-600">
                                        {monthlyProjections.reduce((sum, m) => sum + m.orderQuantity, 0).toLocaleString()}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    
                    {/* Legend */}
                    <div className="px-6 py-3 bg-gray-50 border-t flex items-center gap-6 text-xs text-gray-500">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-50 border border-red-200 rounded"></div>
                            <span>Stockout Risk</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-yellow-50 border border-yellow-200 rounded"></div>
                            <span>Low Stock (&lt;5,000)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-purple-600 font-medium">Arrivals</span>
                            <span>= Orders arriving from previous months</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Daily Forecast Table */}
            {!loading && !error && displayMode === 'table' && viewType === 'daily' && filteredForecasts.length > 0 && (
                <div className="space-y-4">
                    {filteredForecasts.map((forecast) => {
                        const key = `${forecast.product_name}-${forecast.warehouse}`;
                        const isExpanded = expandedProducts.has(key);

                        return (
                            <div key={key} className="bg-white rounded-lg shadow overflow-hidden">
                                {/* Product Header - Clickable */}
                                <div
                                    className="flex items-center justify-between px-6 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => toggleProduct(key)}
                                >
                                    <div className="flex items-center gap-4">
                                        <span className="text-gray-400 text-lg">
                                            {isExpanded ? '▼' : '▶'}
                                        </span>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {forecast.product_name}
                                            </h3>
                                            <div className="text-sm text-gray-500">
                                                {forecast.warehouse} • DRR: {forecast.daily_run_rate.toFixed(1)} •
                                                Growth: {forecast.daily_run_rate > 0 ? ((forecast.demand_growth_rate || 0).toFixed(1) + '%') : '0%'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6 text-sm">
                                        <div className="text-center">
                                            <div className="text-gray-500">Current Stock</div>
                                            <div className="font-bold text-gray-900">
                                                {forecast.current_stock.toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-blue-500">Orders</div>
                                            <div className="font-bold text-blue-600">
                                                {forecast.total_orders}
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-green-500">Total Qty</div>
                                            <div className="font-bold text-green-600">
                                                {forecast.total_quantity.toLocaleString()}
                                            </div>
                                        </div>
                                        {forecast.in_transit_count > 0 && (
                                            <div className="text-center">
                                                <div className="text-purple-500">In Transit</div>
                                                <div className="font-bold text-purple-600">
                                                    {forecast.in_transit_count}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Order Details */}
                                {isExpanded && forecast.forecasted_orders.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order Date</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Arrival Date</th>
                                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Stock Before</th>
                                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Stock After</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Days Away</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quarter</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {forecast.forecasted_orders.map((order) => (
                                                    <tr key={order.order_number} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm text-gray-500">
                                                            {order.order_number}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {formatDate(order.order_date)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {formatDate(order.arrival_date)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                                                            {order.quantity.toLocaleString()}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right text-gray-600">
                                                            {order.stock_before_order.toLocaleString()}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right text-green-600">
                                                            {order.stock_after_arrival.toLocaleString()}
                                                        </td>
                                                        <td className={`px-4 py-3 text-sm ${getDaysColor(order.days_from_now)}`}>
                                                            {order.days_from_now} days
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">
                                                            {order.quarter}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* No Data State */}
            {!loading && !error && filteredForecasts.length === 0 && (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
                    No forecast data available for the selected filters.
                </div>
            )}

            {/* No Data State for Monthly View */}
            {!loading && !error && viewType === 'monthly' && filteredForecasts.length > 0 && monthlyProjections.length === 0 && (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600 mt-4">
                    Unable to generate forecast projection. Please check product data.
                </div>
            )}

            {/* Generated Timestamp */}
            {forecastData && (
                <div className="mt-6 text-xs text-gray-400 text-center">
                    Forecast generated: {new Date(forecastData.generated_at).toLocaleString()}
                </div>
            )}
        </div>
    );
};

export default ForecastView;
